/**
 * Loading a code-split chunk in a way that survives a deploy.
 *
 * ── The incident this exists for ────────────────────────────────────────────
 *
 * Until 2026-08-15 the app had no lazy chunks at all, so a tab someone had open
 * was immune to a deploy. AG Grid then became lazy, and SWA replaces hashed
 * filenames on every deploy — so a stale tab asks for `AgGridInner-<oldhash>.js`,
 * gets a 404, and the dynamic import rejects. With no error boundary that
 * unmounted the whole tree, and it read as an AUTH failure for hours while every
 * API route was returning 200.
 *
 * One reload fixes it, because index.html comes back with the current hashes.
 * The sessionStorage latch is what stops that becoming a reload loop: if the
 * chunk is genuinely missing rather than merely renamed, the second failure
 * inside the window rethrows and an error boundary shows a human a real message
 * instead of refreshing forever in front of them.
 *
 * ── Why it is a shared helper now (phase 4) ─────────────────────────────────
 *
 * It used to live inline in DataTable, next to the one lazy import in the app,
 * with a note in state.md warning that "any new lazy chunk inherits this". Phase 4
 * adds THIRTEEN more — one per route — and a warning that has to be remembered
 * thirteen times is a warning that gets missed once. So the behaviour moved here
 * and both callers use it. There is exactly one latch key and one window, shared
 * across every chunk: a deploy renames all of them at once, so one reload fixes
 * the lot and a per-chunk latch would just permit thirteen reloads in a row.
 */

const CHUNK_RELOAD_KEY = 'purchasing:chunkReloadAt';
const CHUNK_RELOAD_WINDOW_MS = 30_000;

/**
 * Wrap a dynamic `import()` so a chunk that 404s after a deploy reloads the page
 * once, and only once inside the window.
 *
 *     const Page = lazy(() => withChunkRecovery(import('./pages/SpendCube')));
 *
 * The rejection is ALWAYS rethrown, including on the reload path. That is not
 * belt-and-braces: `window.location.reload()` does not stop execution, so
 * swallowing the error here would hand React a resolved-but-empty module and it
 * would render nothing while the reload was still in flight. Rethrowing keeps the
 * boundary the thing that decides what a human sees.
 */
export function withChunkRecovery<T>(p: Promise<T>): Promise<T> {
  return p.catch((err: unknown) => {
    let last = 0;
    try { last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0); } catch { last = 0; }
    if (!Number.isFinite(last) || Date.now() - last > CHUNK_RELOAD_WINDOW_MS) {
      try { sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now())); } catch { /* private mode */ }
      window.location.reload();
    }
    throw err;
  });
}
